import { Table } from "@/components/table";
import { usePosts } from "@/hooks/use-posts";
import { AJAX_ACTION, ENUM, NONCE, OPTIONS, POST_TYPES } from "@/utils/constants";
import { __, sprintf } from "@wordpress/i18n";
import { useCallback, useMemo, useState } from "@wordpress/element";
import { css } from "@/utils/css";
import { ajax } from "@/config/axios";
import { toast } from "react-toastify";
import { Spinner } from "@wordpress/components";
import { Button } from "@/components/button";

const excludePostTypes = [ENUM.POST_TYPE.FORUM, ENUM.POST_TYPE.TOPIC, ENUM.POST_TYPE.REPLY];
const postTypes = POST_TYPES.filter((postType) => postType.hasEditor && !excludePostTypes.includes(postType.id));

import { SimpleProAlert, useProModal } from "@/components/pro-alert-box";
import { useUsageStats } from "@/hooks/use-usage-stats";
import { IS_PRO } from "@/utils/constants";

export default function ConvertSection() {
	const [currentPage, setCurrentPage] = useState(1);
	const [topicsOnProcess, setTopicsOnProcess] = useState<string[]>([]);
	const [selectedPostTypeId, setSelectedPostTypeId] = useState(POST_TYPES[0]?.id);
	const selectedPostType = useMemo(() => postTypes.find((postType) => postType.id === selectedPostTypeId) || postTypes[0], [selectedPostTypeId]);
	const { data: topicData, isLoading: topicsLoading } = usePosts(ENUM.POST_TYPE.TOPIC, currentPage, {
		post_status: ENUM.POST_STATUS.CLOSED,
	});
	const { data: postData, isLoading: postsLoading, refetch } = usePosts(selectedPostType.id);

    const { showProAlert } = useProModal();
    const { data: usageStats, refetch: refetchUsage } = useUsageStats();

	const getGeneratedPost = useCallback(
		(topicId: string) => postData?.posts?.find((post) => post.metadata?.atml_source_topic_id?.[0] === topicId),
		[postData]
	);

	const rows = useMemo(
		() =>
			topicData?.posts?.map((post) => {
				const generatedPost = getGeneratedPost(post.ID.toString());
				const isLoading = topicsOnProcess.includes(post.ID.toString());

				return {
					id: `${post.ID}`,
					topic: (
						<a href={post.permalink} target="_blank">
							{post.post_title || __("(no title)", "antimanual")}
						</a>
					),
					replies: post.reply_count || 0,
					converted_to: (() => {
						if (isLoading || postsLoading) return <Spinner />;

						if (generatedPost) {
							return (
								<a href={generatedPost.permalink} target="_blank">
									{generatedPost.post_title || __("(no title)", "antimanual")}
								</a>
							);
						}

						return (
							<div>
								<Button small className={css(`text-sm py-2 w-auto`)} onClick={() => handleConvert([post.ID.toString()], selectedPostType.id)}>
									{`${__("Convert to", "antimanual")} ${selectedPostType?.label}`}
								</Button>
							</div>
						);
					})(),
					actions: [
						{
							label: __("Edit", "antimanual"),
							url: post.edit_link,
							target: "_blank",
						},
						{
							label: __("View", "antimanual"),
							url: post.permalink,
							target: "_blank",
						},
					],
				};
			}),
		[topicData, postData, getGeneratedPost, topicsOnProcess]
	);

	const columns = useMemo(
		() => [
			{
				id: "topic",
				label: __("Topic", "antimanual"),
				sortable: false,
			},
			{
				id: "replies",
				label: __("Replies", "antimanual"),
				sortable: false,
			},
			{
				id: "converted_to",
				label: __("Converted To", "antimanual"),
				sortable: false,
			},
		],
		[]
	);

	const handleConvert = async (topicIds: string[], postType: string) => {
        if (usageStats?.forum_conversion && usageStats.forum_conversion.count >= usageStats.forum_conversion.limit && !IS_PRO) {
            showProAlert(
                __("Monthly Limit Exceeded", "antimanual"),
                __("You have reached your monthly limit of forum conversions. Upgrade to Pro for unlimited conversions.", "antimanual")
            );
            return;
        }

		await Promise.all(
			topicIds.map(async (topic_id) => {
				setTopicsOnProcess((prev) => [...prev, topic_id]);

				try {
					await ajax({
						action: AJAX_ACTION.CONVERT_TOPIC,
						nonce: NONCE.CONVERT_TOPIC,
						topic_id,
						post_type: postType,
					});

					await refetch().catch(() => {});
                    await refetchUsage();
				} catch (err: any) {
                    if (err?.code === 'monthly_limit_exceeded') {
                        showProAlert(
                            __("Monthly Limit Exceeded", "antimanual"),
                            err?.message
                        );
                    } else {
					    toast.error(err?.message || __("Something went wrong!", "antimanual"));
                    }
				}

				setTopicsOnProcess((prev) => prev.filter((id) => id !== topic_id));
			})
		);
	};

	return (
		<div className={css("p-8")}>
            <div className={css("flex justify-between items-center mb-6")}>
                <div>
                    <span className={css("label")}>{__("Convert to", "antimanual")}</span>
                    <div className={css("btn-group")}>
                        {postTypes.map((postType) => {
                            if (!postType.hasEditor) return null;
                            const isSelected = postType.id === selectedPostType.id;

                            return (
                                <button className={css(isSelected ? "selected" : "")} key={postType.id} onClick={() => setSelectedPostTypeId(postType.id)}>
                                    {__(postType.label, "antimanual")}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {!IS_PRO && usageStats?.forum_conversion && (
                    <SimpleProAlert>
                        {sprintf(
                            __("Usage: %1$d / %2$d", "antimanual"),
                            usageStats.forum_conversion.count,
                            usageStats.forum_conversion.limit
                        )}
                    </SimpleProAlert>
                )}
            </div>

			<Table
				columns={columns}
				rows={rows || []}
				rowsLoading={topicsLoading}
				actions={postTypes
					.filter((postType) => postType.id === selectedPostType.id)
					.map((postType) => ({
						label: `${__("Convert to", "antimanual")} ${postType.label}`,
						value: postType.id,
						handler: (topicIds: string[]) => handleConvert(topicIds, postType.id),
					}))}
				selectedAction={selectedPostType.id}
				onActionSelect={setSelectedPostTypeId}
				pagination={{ total: topicData?.total || 0, currentPage, onPageChange: setCurrentPage, limit: OPTIONS.PAGINATION_LIMIT }}
			/>
		</div>
	);
}
